import { useEffect } from 'react';

/**
 * Mantiene la pantalla encendida mientras la caseta esté a la vista.
 *
 * Una tablet en la reja se apaga sola a los dos minutos, y entonces el guardia
 * tiene que despertarla —y a veces desbloquearla— antes de poder abrir una
 * pluma. Con un coche esperando, esos segundos son justo los que no hay.
 *
 * ## Se suelta y se vuelve a pedir
 *
 * El sistema retira el bloqueo por su cuenta cuando la pestaña deja de verse:
 * al volver de otra app, o de la pantalla de bloqueo, ya no hay bloqueo aunque
 * nadie lo soltara. Por eso se vuelve a pedir en cada `visibilitychange` en vez
 * de confiar en el que se pidió al montar.
 *
 * ## Dónde funciona y dónde no
 *
 * `navigator.wakeLock` **exige contexto seguro**. En el portal servido por HTTP
 * plano no existe, así que aquí no hace nada — y es honesto que no lo haga: no
 * hay forma de mantener la pantalla encendida desde una página insegura, y
 * fingirlo dejaría al guardia creyendo que está resuelto.
 *
 * Dentro del APK **sí funciona**, porque Capacitor sirve la app desde
 * `https://localhost` y eso sí es contexto seguro. Como la tablet de la caseta
 * va a usar el APK, ahí es donde hace falta.
 */
export function usarPantallaEncendida(activo: boolean): void {
  useEffect(() => {
    if (!activo) return;

    // El tipo no está en todas las versiones de TypeScript DOM, y esto tiene
    // que compilar igual donde no exista.
    const nav = navigator as Navigator & {
      wakeLock?: { request: (tipo: 'screen') => Promise<{ release: () => Promise<void> }> };
    };
    if (!nav.wakeLock) return;

    let bloqueo: { release: () => Promise<void> } | null = null;
    let vivo = true;

    async function pedir(): Promise<void> {
      // `document.hidden` se comprueba antes: pedirlo con la pestaña oculta
      // falla, y ese fallo llenaría el registro cada vez que alguien cambia de
      // app sin que haya nada que arreglar.
      if (!vivo || document.hidden || bloqueo) return;
      try {
        bloqueo = (await nav.wakeLock!.request('screen')) ?? null;
      } catch {
        // Batería baja, o el sistema lo negó. No se insiste ni se avisa: la
        // pantalla se apagará como cualquier otra, que es molesto pero no roto.
      }
    }

    function alCambiarVisibilidad(): void {
      // El sistema ya lo soltó por nosotros al ocultarse; lo que queda es
      // olvidarlo para poder pedir otro al volver.
      if (document.hidden) bloqueo = null;
      else void pedir();
    }

    void pedir();
    document.addEventListener('visibilitychange', alCambiarVisibilidad);

    return () => {
      vivo = false;
      document.removeEventListener('visibilitychange', alCambiarVisibilidad);
      void bloqueo?.release().catch(() => undefined);
      bloqueo = null;
    };
  }, [activo]);
}
